import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Clock, Instagram, Loader2, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHATSAPP_URL =
  "https://wa.me/9647760506057?text=%D9%85%D8%B1%D8%AD%D8%A8%D9%8B%D8%A7%20%D9%85%D8%B1%D9%83%D8%B2%20%D9%83%D9%88%D9%86%D9%83%D8%AA%D8%AF%D8%8C%20%D8%A3%D8%B1%D8%BA%D8%A8%20%D8%A8%D9%81%D8%AD%D8%B5%20%D8%B3%D9%8A%D8%A7%D8%B1%D8%AA%D9%8A%20%D9%88%D9%85%D8%B9%D8%B1%D9%81%D8%A9%20%D8%AE%D9%8A%D8%A7%D8%B1%D8%A7%D8%AA%20%D8%A7%D9%84%D8%AA%D8%BA%D9%84%D9%8A%D9%81";

const INFO_ICONS = [MapPin, Clock, Phone, Instagram, Mail, Mail];
const INFO_LINKS = [
  "https://maps.google.com/?q=30.478415,47.820286",
  null,
  "tel:+9647760506057",
  "https://instagram.com/connected_center_basra",
  "mailto:Info@5aegis.com",
  "mailto:Info@eliteworld-iq.com",
];
const INFO_LTR = [false, false, true, true, true, true];

const inputCls =
  "w-full rounded-xl border border-[#F5F2EB]/10 bg-[#000000] px-4 py-3 text-sm text-[#F5F2EB] placeholder:text-[#A8A29E]/50 transition-colors duration-300 focus:border-[#E3AF4F] focus:outline-none focus:ring-1 focus:ring-[#E3AF4F]";

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ full_name: "", phone: "", car: "", serviceIdx: 0, message: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const ltrAlign = lang === "ar" ? "text-right placeholder:text-right" : "text-left placeholder:text-left";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const service = t.contact.services[form.serviceIdx];
      await axios.post(`${API}/contact`, {
        full_name: form.full_name,
        phone: form.phone,
        subject: form.car ? `${service} — ${form.car}` : service,
        message: form.message,
      });
      toast.success(t.contact.successToast);
      setForm({ full_name: "", phone: "", car: "", serviceIdx: 0, message: "" });
    } catch {
      toast.error(t.contact.errorToast);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#0E0E0E]/40 py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="mb-3 flex items-center gap-3 text-xs font-bold tracking-[0.25em] text-[#E3AF4F]">
            <span className="h-px w-10 bg-[#E3AF4F]" />
            {t.contact.eyebrow}
          </p>
          <h2 className="font-heading text-2xl font-bold leading-snug text-[#F5F2EB] sm:text-3xl lg:text-4xl">
            {t.contact.headingA} <span className="text-gold-grad">{t.contact.headingB}</span>
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-[#A8A29E]">
            {t.contact.paragraph}
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#E3AF4F] px-8 py-4 text-sm font-bold text-[#000000] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#F5D08C] hover:shadow-lg hover:shadow-[#E3AF4F]/25"
          >
            <MessageCircle size={18} />
            {t.contact.whatsapp}
          </a>
          <ul className="mt-10 space-y-5">
            {t.contact.info.map((item, i) => {
              const Icon = INFO_ICONS[i];
              const href = INFO_LINKS[i];
              return (
                <li key={item.label} className="flex items-center gap-4" data-testid={`contact-info-${i + 1}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E3AF4F]/25 bg-[#E3AF4F]/10">
                    <Icon size={18} className="text-[#E3AF4F]" />
                  </span>
                  <span>
                    <span className="block text-xs text-[#A8A29E]">{item.label}</span>
                    {href ? (
                      <a
                        href={href}
                        {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        dir={INFO_LTR[i] ? "ltr" : undefined}
                        className="mt-0.5 block text-sm font-semibold text-[#F5F2EB] transition-colors hover:text-[#E3AF4F]"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span className="mt-0.5 block text-sm font-semibold text-[#F5F2EB]">
                        {item.value}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-10 overflow-hidden rounded-2xl border border-[#F5F2EB]/10">
            <iframe
              title="Connected Center — Hamdan Road, Basra"
              src="https://maps.google.com/?q=30.478415,47.820286&output=embed"
              className="h-64 w-full"
              loading="lazy"
              data-testid="contact-map-embed"
            />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="rounded-3xl border border-[#F5F2EB]/10 bg-[#0E0E0E] p-7 sm:p-9"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold text-[#A8A29E]">
                  {t.contact.labels.name}
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-form-name-input"
                  type="text"
                  required
                  minLength={2}
                  value={form.full_name}
                  onChange={update("full_name")}
                  placeholder={t.contact.placeholders.name}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="mb-2 block text-xs font-semibold text-[#A8A29E]">
                  {t.contact.labels.phone}
                </label>
                <input
                  id="contact-phone"
                  data-testid="contact-form-phone-input"
                  type="tel"
                  required
                  minLength={6}
                  dir="ltr"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder={t.contact.placeholders.phone}
                  className={`${inputCls} ${ltrAlign}`}
                />
              </div>
              <div>
                <label htmlFor="contact-car" className="mb-2 block text-xs font-semibold text-[#A8A29E]">
                  {t.contact.labels.car}
                </label>
                <input
                  id="contact-car"
                  data-testid="contact-form-car-input"
                  type="text"
                  value={form.car}
                  onChange={update("car")}
                  placeholder={t.contact.placeholders.car}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-service" className="mb-2 block text-xs font-semibold text-[#A8A29E]">
                  {t.contact.labels.service}
                </label>
                <div className="relative">
                  <select
                    id="contact-service"
                    data-testid="contact-form-service-select"
                    value={form.serviceIdx}
                    onChange={update("serviceIdx")}
                    className={`${inputCls} appearance-none pe-10`}
                  >
                    {t.contact.services.map((s, i) => (
                      <option key={s} value={i} className="bg-[#0E0E0E]">
                        {s}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-[#A8A29E]"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold text-[#A8A29E]">
                  {t.contact.labels.message}
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-form-message-textarea"
                  required
                  minLength={5}
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder={t.contact.placeholders.message}
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-form-submit-button"
              disabled={sending}
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#E3AF4F] py-3.5 text-sm font-bold text-[#000000] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#F5D08C] hover:shadow-lg hover:shadow-[#E3AF4F]/25 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending && <Loader2 size={16} className="animate-spin" />}
              {sending ? t.contact.sending : t.contact.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
